'use client'

import { useTranslation } from "react-i18next";
import { useEffect, useState, useMemo } from "react";
import {
    CaseAdversary,
    add_case_adversary,
    add_adversary,
    update_case_adversary,
    AddAdversary
} from "@/api/case";
import { update_client } from "@/api/client";
import { handleServerError } from "@/utils/handle-server-error";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from '@/hooks/use-toast';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { DatePicker } from '@/components/date-picker';
import { PhoneInput } from '@/views/clients/components/phone-number-input';

// Enums matching client schema
const ClientTypeEnum = z.enum(['individual', 'corporate']);
const IdTypeEnum = z.enum(['cin', 'passport', 'residence_card']);
const LegalFormEnum = z.enum(['sarl', 'sa', 'sas', 'snc', 'auto', 'other']);

// Form schema
const createFormSchema = (t: (key: string) => string) => z.object({
    client_type: ClientTypeEnum,
    name: z.string().min(4, { message: t('client_name_required') }),
    email: z.string().email({ message: t('email_invalid') }).optional().or(z.literal('')),
    phone: z.string().optional().nullable(),
    address: z.string().optional(),
    city: z.string().optional(),
    country: z.string().optional(),
    idnumber: z.string().optional().nullable(),
    id_type: IdTypeEnum.optional().nullable(),
    birth_date: z.string().optional(),
    nationality: z.string().optional(),
    profession: z.string().optional(),
    legal_form: LegalFormEnum.optional().nullable(),
    rc_number: z.string().optional(),
    rc_city: z.string().optional(),
    if_number: z.string().optional(),
    activity_sector: z.string().optional(),
    legal_representative: z.string().optional(),
    representative_role: z.string().optional(),
    company_creation_date: z.string().optional(),
    notes: z.string().optional(),
}).superRefine((data, ctx) => {
    // ICE Validation (Corporate only)
    if (data.client_type === 'corporate') {
        if (data.idnumber && data.idnumber.length > 0) {
            if (!/^\d{15}$/.test(data.idnumber)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: t('ice_invalid'),
                    path: ['idnumber']
                });
            }
        }
    }
});

type AdversaryForm = z.infer<ReturnType<typeof createFormSchema>>;

interface AdversaryActionDialogProps {
    caseId?: number; // Optional: if provided, adds to case. If not, global management (if supported)
    currentRow?: CaseAdversary | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSuccess?: (updatedAdversary?: CaseAdversary) => void;
}

export function AdversaryActionDialog({
    caseId,
    currentRow,
    open,
    onOpenChange,
    onSuccess
}: AdversaryActionDialogProps) {
    const { t, i18n } = useTranslation();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const formSchema = useMemo(() => createFormSchema(t), [t]);

    const form = useForm<AdversaryForm>({
        resolver: zodResolver(formSchema),
        mode: "onChange",
        defaultValues: {
            client_type: 'individual',
            name: '',
            email: '',
            phone: '',
            idnumber: '',
        },
    });

    useEffect(() => {
        if (currentRow) {
            form.reset({
                client_type: currentRow.client.client_type || 'individual',
                name: currentRow.client.name,
                email: currentRow.client.email || '',
                phone: currentRow.client.phone || '',
                address: currentRow.client.address || '',
                city: currentRow.client.city || '',
                country: currentRow.client.country || '',
                idnumber: currentRow.client.idnumber || '',
                id_type: (currentRow.client.id_type as any) || null,
                birth_date: currentRow.client.birth_date || '',
                nationality: currentRow.client.nationality || '',
                profession: currentRow.client.profession || '',
                legal_form: (currentRow.client.legal_form as any) || null,
                rc_number: currentRow.client.rc_number || '',
                rc_city: currentRow.client.rc_city || '',
                if_number: currentRow.client.if_number || '',
                activity_sector: currentRow.client.activity_sector || '',
                legal_representative: currentRow.client.legal_representative || '',
                representative_role: currentRow.client.representative_role || '',
                company_creation_date: currentRow.client.company_creation_date || '',
                notes: currentRow.client.notes || '',
            });
        } else {
            form.reset({
                client_type: 'individual',
                name: '',
                email: '',
                phone: '',
                idnumber: '',
            });
        }
    }, [currentRow, form, open]);

    const clientType = form.watch('client_type');

    const handleClose = () => {
        onOpenChange(false);
    };

    const onSubmit = async (values: AdversaryForm) => {
        setIsSubmitting(true);

        // Filter out empty strings for cleaner data
        const cleanValues = Object.fromEntries(
            Object.entries(values).map(([k, v]) => [k, v === '' ? null : v])
        ) as unknown as AddAdversary;

        try {
            if (currentRow) {
                let updated;
                if (caseId) {
                    updated = await update_case_adversary(currentRow.id, cleanValues);
                } else {
                    // Global edit - use ID from normalized object (which is client ID)
                    // Cast cleanValues to any because update_client expects AddClient but AddAdversary is compatible
                    updated = await update_client(currentRow.id, cleanValues as any);
                }

                toast({
                    title: t('adversary_updated', { name: values.name }),
                });
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onSuccess?.(updated as any);
            } else if (caseId) {
                const created = await add_case_adversary(caseId, cleanValues);
                toast({
                    title: t('adversary_added', { name: values.name }),
                });
                onSuccess?.(created);
            } else {
                const created = await add_adversary(cleanValues);
                toast({
                    title: t('adversary_added', { name: values.name }),
                });
                onSuccess?.(created);
            }
            handleClose();
        } catch (e) {
            handleServerError(e);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className='sm:max-w-xl max-h-[90vh] overflow-y-auto' dir={i18n.dir()}>
                <DialogHeader className='text-start'>
                    <DialogTitle>{currentRow ? t('edit_adversary') : t('add_adversary')}</DialogTitle>
                    <DialogDescription className="text-start">
                        {currentRow ? t('edit_adversary_desc') : t('add_adversary_desc')}
                    </DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <form id='adversary-form' onSubmit={form.handleSubmit(onSubmit, (errors) => {
                        const messages = Object.values(errors)
                            .map((e: any) => e?.message)
                            .filter(Boolean) as string[]
                        toast({
                            title: t('validation_failed'),
                            description: messages.join(' · '),
                            variant: 'destructive',
                        })
                    })} className='space-y-6'>

                        {/* 1. Client Type Selection */}
                        <FormField
                            control={form.control}
                            name="client_type"
                            render={({ field }) => (
                                <Tabs value={field.value} onValueChange={field.onChange} className="w-full">
                                    <TabsList className="grid w-full grid-cols-2">
                                        <TabsTrigger value="individual">{t('individual')}</TabsTrigger>
                                        <TabsTrigger value="corporate">{t('corporate')}</TabsTrigger>
                                    </TabsList>
                                </Tabs>
                            )}
                        />

                        <div className="space-y-4">
                            {/* 2. General Information Section */}
                            <div>
                                <h3 className="text-sm font-medium text-muted-foreground mb-3">{t('general_info')}</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <FormField
                                        control={form.control}
                                        name='name'
                                        render={({ field }) => (
                                            <FormItem className="col-span-2">
                                                <FormLabel>{t('adversary_name')}</FormLabel>
                                                <FormControl>
                                                    <Input placeholder={t('name_placeholder')} {...field} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={form.control}
                                        name='email'
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>{t('email')}</FormLabel>
                                                <FormControl>
                                                    <Input placeholder='adversary@example.com' {...field} value={field.value || ''} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={form.control}
                                        name='phone'
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>{t('phone')}</FormLabel>
                                                <FormControl>
                                                    <PhoneInput {...field} value={field.value || ''} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={form.control}
                                        name='address'
                                        render={({ field }) => (
                                            <FormItem className="col-span-2">
                                                <FormLabel>{t('address')}</FormLabel>
                                                <FormControl>
                                                    <Input placeholder={t('address_placeholder')} {...field} value={field.value || ''} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={form.control}
                                        name='city'
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>{t('city')}</FormLabel>
                                                <FormControl>
                                                    <Input placeholder={t('city')} {...field} value={field.value || ''} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={form.control}
                                        name='country'
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>{t('country')}</FormLabel>
                                                <FormControl>
                                                    <Input placeholder={t('country')} {...field} value={field.value || ''} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </div>

                            <Separator />

                            {/* 3. Specific Information Section */}
                            <div>
                                <h3 className="text-sm font-medium text-muted-foreground mb-3">{t('specific_info')}</h3>

                                {clientType === 'individual' ? (
                                    /* INDIVIDUAL FIELDS */
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <FormField
                                            control={form.control}
                                            name='id_type'
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>{t('id_type')}</FormLabel>
                                                    <Select onValueChange={field.onChange} defaultValue={field.value || undefined}>
                                                        <FormControl>
                                                            <SelectTrigger>
                                                                <SelectValue placeholder={t('select')} />
                                                            </SelectTrigger>
                                                        </FormControl>
                                                        <SelectContent>
                                                            <SelectItem value="cin">{t('cin')}</SelectItem>
                                                            <SelectItem value="passport">{t('passport')}</SelectItem>
                                                            <SelectItem value="residence_card">{t('residence_card')}</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name='idnumber'
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>{t('id_number')}</FormLabel>
                                                    <FormControl>
                                                        <Input placeholder={t('idnumber_placeholder')} {...field} value={field.value || ''} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name='birth_date'
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>{t('birth_date')}</FormLabel>
                                                    <FormControl>
                                                        <DatePicker
                                                            date={field.value ? new Date(field.value) : undefined}
                                                            setDate={(date) => field.onChange(date ? date.toISOString().split('T')[0] : '')}
                                                            placeholder={t('pick_a_date')}
                                                            maxDate={new Date()}
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name='nationality'
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>{t('nationality')}</FormLabel>
                                                    <FormControl>
                                                        <Input {...field} value={field.value || ''} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name='profession'
                                            render={({ field }) => (
                                                <FormItem className="col-span-2">
                                                    <FormLabel>{t('profession')}</FormLabel>
                                                    <FormControl>
                                                        <Input {...field} value={field.value || ''} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                ) : (
                                    /* CORPORATE FIELDS */
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <FormField
                                            control={form.control}
                                            name='legal_form'
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>{t('legal_form')}</FormLabel>
                                                    <Select onValueChange={field.onChange} defaultValue={field.value || undefined} value={field.value || undefined}>
                                                        <FormControl>
                                                            <SelectTrigger>
                                                                <SelectValue placeholder={t('select')} />
                                                            </SelectTrigger>
                                                        </FormControl>
                                                        <SelectContent>
                                                            <SelectItem value="sarl">SARL</SelectItem>
                                                            <SelectItem value="sa">SA</SelectItem>
                                                            <SelectItem value="sas">SAS</SelectItem>
                                                            <SelectItem value="snc">SNC</SelectItem>
                                                            <SelectItem value="auto">Auto-Entrepreneur</SelectItem>
                                                            <SelectItem value="other">{t('other')}</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name='idnumber'
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>ICE</FormLabel>
                                                    <FormControl>
                                                        <Input placeholder={t('ice_placeholder')} {...field} value={field.value || ''} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name='rc_number'
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>{t('rc_number')}</FormLabel>
                                                    <FormControl>
                                                        <Input {...field} value={field.value || ''} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name='rc_city'
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>{t('rc_city')}</FormLabel>
                                                    <FormControl>
                                                        <Input {...field} value={field.value || ''} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name='if_number'
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>{t('if_number')}</FormLabel>
                                                    <FormControl>
                                                        <Input {...field} value={field.value || ''} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name='activity_sector'
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>{t('activity_sector')}</FormLabel>
                                                    <FormControl>
                                                        <Input {...field} value={field.value || ''} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name='legal_representative'
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>{t('legal_representative')}</FormLabel>
                                                    <FormControl>
                                                        <Input {...field} value={field.value || ''} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name='representative_role'
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>{t('representative_role')}</FormLabel>
                                                    <FormControl>
                                                        <Input {...field} value={field.value || ''} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                )}
                            </div>

                            <Separator />

                            {/* 4. Notes Section */}
                            <div>
                                <h3 className="text-sm font-medium text-muted-foreground mb-3">{t('internal_notes')}</h3>
                                <FormField
                                    control={form.control}
                                    name='notes'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormControl>
                                                <Textarea placeholder={t('notes')} className="min-h-[100px]" {...field} value={field.value || ''} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                        </div>

                    </form>
                </Form>
                <DialogFooter>
                    <Button type='submit' form='adversary-form' disabled={isSubmitting}>
                        {isSubmitting ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                {t('saving')}
                            </>
                        ) : (
                            t('save')
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
